import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Shield, Users, MapPin, Mail, Phone, ArrowRight, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { CityData } from "@/utils/cityData";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
interface TariffCalculatorProps {
  city: CityData;
}
const TariffCalculator = ({
  city
}: TariffCalculatorProps) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    practiceType: "",
    employees: 3,
    postalCode: "",
    email: "",
    phone: ""
  });
  const totalSteps = 4;
  const progress = step / totalSteps * 100;
  const handleNext = () => {
    if (step === 1 && !formData.practiceType) {
      toast.error("Bitte wählen Sie eine Praxis-Art aus");
      return;
    }
    if (step === 3 && !formData.postalCode) {
      toast.error("Bitte geben Sie Ihre PLZ ein");
      return;
    }
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.phone) {
      toast.error("Bitte füllen Sie alle Kontaktfelder aus");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const { error } = await supabase.functions.invoke('send-consultation-request', {
        body: {
          name: `${formData.practiceType} - ${city.name}`,
          email: formData.email,
          phone: formData.phone,
          message: `Tarif-Rechner Anfrage für ${city.name}`,
          
          // Additional data for lead storage
          practiceType: formData.practiceType,
          employees: formData.employees,
          postalCode: formData.postalCode,
          city: city.name,
          source: 'tariff_calculator'
        },
      });

      if (error) throw error;

      toast.success("Vielen Dank! Wir berechnen Ihren individuellen Tarif und melden uns in Kürze.");
      
      // Reset form
      setFormData({
        practiceType: "",
        employees: 3,
        postalCode: "",
        email: "",
        phone: ""
      });
      setStep(1);
      
    } catch (error) {
      console.error('Error submitting tariff request:', error);
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section className="relative overflow-hidden">
      {/* Dark Vignette */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)] pointer-events-none" />
      
      <div className="container relative mx-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-4 sm:mb-6 animate-fade-in">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-2 text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
              Angebot in 60 Sekunden
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground/80">
              Kostenlos & unverbindlich
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="flex justify-between items-center mb-2 text-xs sm:text-sm">
              <span className="font-medium text-foreground/90">Schritt {step} von {totalSteps}</span>
              <span className="text-muted-foreground/80">{Math.round(progress)}%</span>
            </div>
          <div className="relative h-2 bg-black/30 rounded-full overflow-hidden backdrop-blur-sm border border-black/20">
            <div className="absolute inset-0 bg-gradient-to-r from-white/80 to-white/60 rounded-full transition-all duration-500 ease-out shadow-[0_2px_8px_rgba(255,255,255,0.3),inset_0_1px_2px_rgba(0,0,0,0.3)]" style={{
              width: `${progress}%`
            }} />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* Step 1: Practice Type */}
            {step >= 1 && <div className={`
                  group relative
                  bg-gradient-to-br from-card/30 via-card/20 to-card/10
                  backdrop-blur-[24px]
                  border border-black/30
                  rounded-2xl
                  shadow-[0_8px_32px_0_rgba(0,0,0,0.37),inset_0_1px_1px_0_rgba(0,0,0,0.15)]
                  hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.5),inset_0_1px_1px_0_rgba(0,0,0,0.2)]
                  hover:border-white/30
                  hover:-translate-y-1
                  transition-all duration-500 ease-out
                  ${step > 1 ? 'opacity-50 scale-[0.98] saturate-50' : 'opacity-100 scale-100 saturate-100'}
                  animate-fade-in
                `} style={{
            animationDelay: '100ms'
          }}>
                <div className="p-4 sm:p-5 lg:p-6">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-white/20 to-white/10 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2),inset_-2px_-2px_4px_rgba(0,0,0,0.4)] flex items-center justify-center border border-white/20">
                      <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground mb-1">
                        Welche Art von Praxis betreiben Sie?
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Optimaler Versicherungsschutz
                      </p>
                    </div>
                  </div>
                  
                  {step === 1 ? <div className="space-y-2.5">
                      <Label htmlFor="practiceType" className="text-sm font-medium">Praxis-Art *</Label>
                      <Select value={formData.practiceType} onValueChange={value => setFormData({
                  ...formData,
                  practiceType: value
                })}>
                        <SelectTrigger id="practiceType" className="h-11 sm:h-12 bg-black/20 border border-white/20 backdrop-blur-md rounded-lg hover:border-white/40 focus:border-white/60 focus:shadow-[0_0_16px_rgba(255,255,255,0.2)] transition-all duration-300 text-foreground min-h-[44px]">
                          <SelectValue placeholder="Bitte auswählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hausarzt">Hausarztpraxis / Allgemeinmedizin</SelectItem>
                          <SelectItem value="zahnarzt">Zahnarztpraxis</SelectItem>
                          <SelectItem value="facharzt">Facharztpraxis</SelectItem>
                          <SelectItem value="mvz">Medizinisches Versorgungszentrum (MVZ)</SelectItem>
                          <SelectItem value="klinik">Klinik / Krankenhaus</SelectItem>
                        </SelectContent>
                      </Select>
                    </div> : <div className="flex items-center gap-2 text-sm text-muted-foreground bg-white/5 rounded-lg px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      <span className="truncate">
                        {formData.practiceType === 'hausarzt' ? 'Hausarztpraxis' : formData.practiceType === 'zahnarzt' ? 'Zahnarztpraxis' : formData.practiceType === 'facharzt' ? 'Facharztpraxis' : formData.practiceType === 'mvz' ? 'MVZ' : 'Klinik'}
                      </span>
                    </div>}
                </div>
              </div>}

            {/* Step 2: Employees */}
            {step >= 2 && <div className={`
                  group relative
                  bg-gradient-to-br from-card/30 via-card/20 to-card/10
                  backdrop-blur-[24px]
                  border border-black/30
                  rounded-2xl
                  shadow-[0_8px_32px_0_rgba(0,0,0,0.37),inset_0_1px_1px_0_rgba(0,0,0,0.15)]
                  hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.5),inset_0_1px_1px_0_rgba(0,0,0,0.2)]
                  hover:border-white/30
                  hover:-translate-y-1
                  transition-all duration-500 ease-out
                  ${step > 2 ? 'opacity-50 scale-[0.98] saturate-50' : 'opacity-100 scale-100 saturate-100'}
                  animate-fade-in
                `} style={{
            animationDelay: '200ms'
          }}>
                <div className="p-4 sm:p-5 lg:p-6">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-white/20 to-white/10 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2),inset_-2px_-2px_4px_rgba(0,0,0,0.4)] flex items-center justify-center border border-white/20">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground mb-1">
                        Wie viele Mitarbeiter beschäftigen Sie?
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Inklusive Ärzte, med. Personal und Verwaltung
                      </p>
                    </div>
                  </div>
                  
                  {step === 2 ? <div className="space-y-4">
                      <div className="text-center py-2">
                        <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                          {formData.employees}
                        </span>
                        <span className="text-sm sm:text-base lg:text-lg text-muted-foreground ml-2">
                          Mitarbeiter
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        <Slider value={[formData.employees]} onValueChange={value => setFormData({
                    ...formData,
                    employees: value[0]
                  })} min={1} max={50} step={1} className="w-full touch-none py-2" />
                        
                        <div className="flex justify-between text-xs text-muted-foreground px-1">
                          <span>1</span>
                          <span>50+</span>
                        </div>
                      </div>
                    </div> : <div className="flex items-center gap-2 text-sm text-muted-foreground bg-white/5 rounded-lg px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      <span>{formData.employees} Mitarbeiter</span>
                    </div>}
                </div>
              </div>}

            {/* Step 3: Postal Code */}
            {step >= 3 && <div className={`
                  group relative
                  bg-gradient-to-br from-card/30 via-card/20 to-card/10
                  backdrop-blur-[24px]
                  border border-black/30
                  rounded-2xl
                  shadow-[0_8px_32px_0_rgba(0,0,0,0.37),inset_0_1px_1px_0_rgba(0,0,0,0.15)]
                  hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.5),inset_0_1px_1px_0_rgba(0,0,0,0.2)]
                  hover:border-white/30
                  hover:-translate-y-1
                  transition-all duration-500 ease-out
                  ${step > 3 ? 'opacity-50 scale-[0.98] saturate-50' : 'opacity-100 scale-100 saturate-100'}
                  animate-fade-in
                `} style={{
            animationDelay: '300ms'
          }}>
                <div className="p-4 sm:p-5 lg:p-6">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-white/20 to-white/10 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2),inset_-2px_-2px_4px_rgba(0,0,0,0.4)] flex items-center justify-center border border-white/20">
                      <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground mb-1">
                        Wo befindet sich Ihre Praxis?
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Für regionsspezifische Beratung
                      </p>
                    </div>
                  </div>
                  
                  {step === 3 ? <div className="space-y-2.5">
                      <Label htmlFor="postalCode" className="text-sm font-medium">Postleitzahl *</Label>
                      <Input id="postalCode" type="text" placeholder={`z.B. ${city.name} PLZ`} value={formData.postalCode} onChange={e => setFormData({
                  ...formData,
                  postalCode: e.target.value
                })} className="h-11 sm:h-12 bg-black/20 border border-white/20 backdrop-blur-md rounded-lg hover:border-white/40 focus:border-white/60 focus:shadow-[0_0_16px_rgba(255,255,255,0.2)] transition-all duration-300 text-foreground placeholder:text-muted-foreground/50 min-h-[44px]" maxLength={5} />
                    </div> : <div className="flex items-center gap-2 text-sm text-muted-foreground bg-white/5 rounded-lg px-3 py-2">
                      <CheckCircle2 className="w-4 h-4 text-white flex-shrink-0" />
                      <span>PLZ {formData.postalCode}</span>
                    </div>}
                </div>
              </div>}

            {/* Step 4: Contact */}
            {step >= 4 && <div className={`
                  group relative
                  bg-gradient-to-br from-card/30 via-card/20 to-card/10
                  backdrop-blur-[24px]
                  border border-black/30
                  rounded-2xl
                  shadow-[0_8px_32px_0_rgba(0,0,0,0.37),inset_0_1px_1px_0_rgba(0,0,0,0.15)]
                  hover:shadow-[0_8px_32px_0_rgba(0,0,0,0.5),inset_0_1px_1px_0_rgba(0,0,0,0.2)]
                  hover:border-white/30
                  hover:-translate-y-1
                  transition-all duration-500 ease-out
                  animate-fade-in
                `} style={{
            animationDelay: '400ms'
          }}>
                <div className="p-4 sm:p-5 lg:p-6">
                  <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-white/20 to-white/10 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.2),inset_-2px_-2px_4px_rgba(0,0,0,0.4)] flex items-center justify-center border border-white/20">
                      <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base lg:text-lg font-bold text-foreground mb-1">
                        Letzter Schritt: Ihre Kontaktdaten
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Für Ihr individuelles Angebot
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-3.5">
                    <div className="space-y-2.5">
                      <Label htmlFor="email" className="text-sm font-medium flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5" />
                        E-Mail-Adresse *
                      </Label>
                      <Input id="email" type="email" placeholder="ihre@praxis.de" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="h-11 sm:h-12 bg-black/20 border border-white/20 backdrop-blur-md rounded-lg hover:border-white/40 focus:border-white/60 focus:shadow-[0_0_16px_rgba(255,255,255,0.2)] transition-all duration-300 text-foreground placeholder:text-muted-foreground/50 min-h-[44px]" required />
                    </div>

                    <div className="space-y-2.5">
                      <Label htmlFor="phone" className="text-sm font-medium flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5" />
                        Telefonnummer *
                      </Label>
                      <Input id="phone" type="tel" placeholder="030 / 123 456 789" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className="h-11 sm:h-12 bg-black/20 border border-black/20 backdrop-blur-md rounded-lg hover:border-primary/50 focus:border-primary focus:shadow-[0_0_16px_rgba(var(--primary),0.3)] transition-all duration-300 text-foreground placeholder:text-muted-foreground/50 min-h-[44px]" required />
                    </div>

                    {/* Trust Elements */}
                    <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pt-3 mt-3 text-xs border-t border-border/30">
                      <span className="flex items-center gap-1.5 text-muted-foreground/80">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        100% kostenlos
                      </span>
                      <span className="flex items-center gap-1.5 text-muted-foreground/80">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        Unverbindlich
                      </span>
                      <span className="flex items-center gap-1.5 text-muted-foreground/80">
                        <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        DSGVO-konform
                      </span>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-2">
              {step > 1 && <Button type="button" onClick={handleBack} className="flex-1 h-11 sm:h-12 text-xs sm:text-sm font-medium bg-transparent text-white border-2 border-accent rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:bg-black/30 hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80 hover:scale-[1.02] transition-all duration-300 backdrop-blur-sm min-h-[44px] text-center my-[30px] mx-[20px]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>}
              
              {step < totalSteps ? <Button type="button" onClick={handleNext} className="flex-1 h-11 sm:h-12 text-xs sm:text-sm font-medium bg-transparent text-white border-2 border-accent rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:bg-black/30 hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80 hover:scale-[1.02] transition-all duration-300 backdrop-blur-sm min-h-[44px] text-center my-[30px]">
                  Weiter
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button> : <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="flex-1 h-11 sm:h-12 text-xs sm:text-sm font-medium bg-transparent text-white border-2 border-accent rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.3)] hover:bg-black/30 hover:shadow-[0_0_20px_rgba(139,195,74,0.5)] hover:border-accent/80 hover:scale-[1.02] transition-all duration-300 backdrop-blur-sm min-h-[44px] text-center my-[30px] py-0 mx-[20px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird verarbeitet...
                    </>
                  ) : (
                    <>
                      Jetzt individuelles Angebot anfordern
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>}
            </div>
          </form>
        </div>
      </div>
    </section>;
};
export default TariffCalculator;